import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, LayoutGrid, List as ListIcon, Plus, Users } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { salesApi, apiError } from '../salesApi';
import { useT } from '../salesI18n';
import { useSalesAuth } from '../SalesAuthContext';
import { STATUS_ORDER } from '../components/StatusBadge';
import { LeadCard } from '../components/LeadCard';
import { LeadKanban } from '../components/LeadKanban';
import { EmptyState } from '../components/EmptyState';

const SOURCES = ['referral', 'website', 'phone', 'door_to_door', 'event', 'social', 'other'];
const VIEW_KEY = 'sales_leads_view';

export default function LeadsPage() {
  const { t } = useT();
  const { user } = useSalesAuth();
  const { openNewLead } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [leads, setLeads] = useState(null);
  const [team, setTeam] = useState([]);
  const [q, setQ] = useState(params.get('q') || '');
  const [view, setView] = useState(localStorage.getItem(VIEW_KEY) || 'list');
  const status = params.get('status') || 'all';
  const source = params.get('source') || 'all';
  const rep = params.get('rep') || 'all';

  const setParam = (k, v) => { const p = new URLSearchParams(params); if (!v || v === 'all') p.delete(k); else p.set(k, v); setParams(p, { replace: true }); };

  const load = useCallback(async () => {
    const query = {};
    if (status !== 'all' && view === 'list') query.status = status;
    if (source !== 'all') query.source = source;
    if (rep !== 'all') query.assigned_to = rep;
    if (q.trim()) query.q = q.trim();
    try { const { data } = await salesApi.get('/leads', { params: query }); setLeads(data); } catch (e) { toast.error(apiError(e)); setLeads([]); }
  }, [status, source, rep, q, view]);

  useEffect(() => { const id = setTimeout(load, q ? 300 : 0); return () => clearTimeout(id); }, [load, q]);
  useEffect(() => { if (user?.role === 'manager') salesApi.get('/team').then((r) => setTeam(r.data)).catch(() => {}); }, [user]);

  const switchView = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };

  const moveLead = async (lead, newStatus) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: newStatus } : l)));
    try {
      await salesApi.patch(`/leads/${lead.id}/status`, { status: newStatus });
      toast.success(`${lead.customer_name}: ${t.status[newStatus]}`);
    } catch (e) { setLeads(prev); toast.error(apiError(e)); }
  };

  const grouped = useMemo(() => leads || [], [leads]);

  return (
    <div className="space-y-5" data-testid="sales-leads-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">CRM</p>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">{t.leads} <span className="text-muted-foreground font-mono text-base align-middle" data-testid="leads-count">{leads ? t.leadsCount(leads.length) : ''}</span></h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-white/80 p-1 bg-white/60 backdrop-blur" data-testid="leads-view-toggle">
            <button type="button" onClick={() => switchView('list')} data-testid="leads-view-list" className={`h-9 px-4 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-all ${view === 'list' ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white/70'}`}><ListIcon className="h-4 w-4" /> {t.list}</button>
            <button type="button" onClick={() => switchView('kanban')} data-testid="leads-view-kanban" className={`h-9 px-4 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-all ${view === 'kanban' ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white/70'}`}><LayoutGrid className="h-4 w-4" /> {t.kanban}</button>
          </div>
          <Button onClick={openNewLead} className="h-10 font-semibold hidden sm:inline-flex" data-testid="leads-new-lead-btn"><Plus className="h-4 w-4 mr-1.5" /> {t.newLead}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="leads-search-input" value={q} onChange={(e) => { setQ(e.target.value); setParam('q', e.target.value); }} placeholder={t.search} className="pl-9 h-11" />
        </div>
        {view === 'list' && (
          <Select value={status} onValueChange={(v) => setParam('status', v)}>
            <SelectTrigger data-testid="leads-filter-status" className="h-11 sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.allStatuses}</SelectItem>
              {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{t.status[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={source} onValueChange={(v) => setParam('source', v)}>
          <SelectTrigger data-testid="leads-filter-source" className="h-11 sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allSources}</SelectItem>
            {SOURCES.map((s) => <SelectItem key={s} value={s}>{t.source[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        {user?.role === 'manager' && (
          <Select value={rep} onValueChange={(v) => setParam('rep', v)}>
            <SelectTrigger data-testid="leads-filter-rep" className="h-11 sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.everyone}</SelectItem>
              {team.map((m) => <SelectItem key={m.username} value={m.username}>{m.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {leads === null ? (
        <p className="text-sm text-muted-foreground" data-testid="leads-loading">{t.loading}</p>
      ) : leads.length === 0 ? (
        <EmptyState icon={Users} title={t.noLeads} hint={t.noLeadsHint} action={<Button onClick={openNewLead} className="h-11" data-testid="leads-empty-new-lead"><Plus className="h-4 w-4 mr-1.5" />{t.newLead}</Button>} testId="leads-empty" />
      ) : view === 'kanban' ? (
        <LeadKanban leads={grouped} onMove={moveLead} />
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="leads-list">
          {grouped.map((l, i) => <div key={l.id} className={`sales-rise ${i < 4 ? `sales-rise-${i}` : ''}`}><LeadCard lead={l} /></div>)}
        </div>
      )}
    </div>
  );
}
